import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createCustomerETLApi,
  getAvailableOCELFiles,
  getActiveOCELFile,
  setActiveOCELFile,
  refreshETLFiles,
} from '../api/etlServiceApi';
import { getStoredActiveFile, setStoredActiveFile } from '../../../services/activeFileStorage';

const POLL_INTERVAL_MS = 60000; // health + jobs refresh every 60 s

/**
 * @param {object} opts
 * @param {string|null} opts.customerId   — active customer ID (null = no customer selected)
 * @param {Function}    [opts.onFileActivated]
 */
export function useETLService({ customerId, onFileActivated } = {}) {
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [etlFiles, setEtlFiles] = useState([]);
  const [availableFiles, setAvailableFiles] = useState([]);
  const [activeFileType, setActiveFileTypeState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [etlServiceReachable, setEtlServiceReachable] = useState(false);
  const [triggeringJob, setTriggeringJob] = useState(false);
  const [activatingFile, setActivatingFile] = useState(null);
  const [refreshingFiles, setRefreshingFiles] = useState(false);
  const pollingJobId = useRef(null);
  const pollingTimer = useRef(null);

  // ── Initial load ────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    // Always fetch main backend data — these don't require a customer connection.
    const [availResult, activeResult] = await Promise.allSettled([
      getAvailableOCELFiles(),
      getActiveOCELFile(),
    ]);
    if (availResult.status === 'fulfilled') setAvailableFiles(Array.isArray(availResult.value) ? availResult.value : []);
    if (activeResult.status === 'fulfilled')
      // This browser's selection wins; the server value is only the
      // first-visit default.
      setActiveFileTypeState(
        getStoredActiveFile() ?? activeResult.value?.active_file_type ?? null,
      );

    if (!customerId) {
      setHealth(null);
      setJobs([]);
      setSchedule(null);
      setEtlFiles([]);
      setEtlServiceReachable(false);
      setLoading(false);
      return;
    }

    const api = createCustomerETLApi(customerId);

    const [healthResult, jobsResult, scheduleResult, etlFilesResult] =
      await Promise.allSettled([
        api.getHealth(),
        api.getJobs(20),
        api.getSchedule(),
        api.getOCELFiles(),
      ]);

    if (healthResult.status === 'fulfilled') {
      setHealth(healthResult.value);
      setEtlServiceReachable(true);
    } else {
      setHealth(null);
      setEtlServiceReachable(false);
    }

    if (jobsResult.status === 'fulfilled') setJobs(Array.isArray(jobsResult.value) ? jobsResult.value : []);
    if (scheduleResult.status === 'fulfilled') setSchedule(scheduleResult.value);
    if (etlFilesResult.status === 'fulfilled') setEtlFiles(Array.isArray(etlFilesResult.value) ? etlFilesResult.value : []);

    setLoading(false);
  }, [customerId]);

  useEffect(() => {
    setLoading(true);
    loadAll();
    pollingTimer.current = setInterval(loadAll, POLL_INTERVAL_MS);
    return () => clearInterval(pollingTimer.current);
  }, [loadAll]);

  // ── Active job polling ──────────────────────────────────────────────────────

  const pollJob = useCallback(
    (jobId) => {
      if (!customerId) return;
      const api = createCustomerETLApi(customerId);
      pollingJobId.current = jobId;
      const timer = setInterval(async () => {
        try {
          const job = await api.getJob(jobId);
          setJobs((prev) => prev.map((j) => (j.job_id === jobId ? job : j)));
          if (job.status === 'success' || job.status === 'failed') {
            clearInterval(timer);
            pollingJobId.current = null;
            setTriggeringJob(false);
            if (job.status === 'success') {
              await refreshETLFiles();
              const [etlFilesRes, availRes] = await Promise.allSettled([
                api.getOCELFiles(),
                getAvailableOCELFiles(),
              ]);
              if (etlFilesRes.status === 'fulfilled') setEtlFiles(etlFilesRes.value);
              if (availRes.status === 'fulfilled') setAvailableFiles(availRes.value);
            }
          }
        } catch {
          clearInterval(timer);
          pollingJobId.current = null;
          setTriggeringJob(false);
        }
      }, 2000);
    },
    [customerId],
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  const triggerJob = useCallback(
    async (options = {}) => {
      if (!customerId) return;
      const api = createCustomerETLApi(customerId);
      setTriggeringJob(true);
      try {
        const res = await api.triggerRun(options);
        const newJob = {
          job_id: res.job_id,
          status: 'pending',
          started_at: new Date().toISOString(),
          rows_extracted: 0,
        };
        setJobs((prev) => [newJob, ...prev]);
        pollJob(res.job_id);
        return res.job_id;
      } catch (err) {
        setTriggeringJob(false);
        throw err;
      }
    },
    [customerId, pollJob],
  );

  const activateFile = useCallback(
    async (fileType) => {
      setActivatingFile(fileType);
      try {
        // Persist this browser's selection; the POST below only updates the
        // server-side default used for first-time visitors.
        setStoredActiveFile(fileType);
        await setActiveOCELFile(fileType);
        setActiveFileTypeState(fileType);
        onFileActivated?.(fileType);
      } finally {
        setActivatingFile(null);
      }
    },
    [onFileActivated],
  );

  const refreshSchedule = useCallback(async () => {
    if (!customerId) return;
    const api = createCustomerETLApi(customerId);
    try {
      const result = await api.getSchedule();
      setSchedule(result);
    } catch {
      // transient — keep showing the last known schedule
    }
  }, [customerId]);

  const refreshFiles = useCallback(async () => {
    setRefreshingFiles(true);
    try {
      if (customerId) {
        const api = createCustomerETLApi(customerId);
        await refreshETLFiles();
        const [etlFilesRes, availRes, activeRes] = await Promise.allSettled([
          api.getOCELFiles(),
          getAvailableOCELFiles(),
          getActiveOCELFile(),
        ]);
        if (etlFilesRes.status === 'fulfilled') setEtlFiles(etlFilesRes.value);
        if (availRes.status === 'fulfilled') setAvailableFiles(availRes.value);
        if (activeRes.status === 'fulfilled')
          setActiveFileTypeState(
            getStoredActiveFile() ?? activeRes.value?.active_file_type ?? null,
          );
      } else {
        const [availRes, activeRes] = await Promise.allSettled([
          getAvailableOCELFiles(),
          getActiveOCELFile(),
        ]);
        if (availRes.status === 'fulfilled') setAvailableFiles(availRes.value);
        if (activeRes.status === 'fulfilled')
          setActiveFileTypeState(
            getStoredActiveFile() ?? activeRes.value?.active_file_type ?? null,
          );
      }
    } finally {
      setRefreshingFiles(false);
    }
  }, [customerId]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const etlGeneratedTypes = new Set(etlFiles.map((f) => f.file_type));
  const builtInFiles = availableFiles.filter((f) => !etlGeneratedTypes.has(f.file_type));

  return {
    health,
    jobs,
    schedule,
    etlFiles,
    builtInFiles,
    activeFileType,
    loading,
    etlServiceReachable,
    triggeringJob,
    activatingFile,
    refreshingFiles,
    triggerJob,
    activateFile,
    refreshFiles,
    refreshSchedule,
  };
}
